import React from "react";
import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Button,
    Alert,
} from "@material-tailwind/react";
import { Form, Formik } from "formik";
import FormikInput from "components/form/formik-input";
import * as Yup from "yup";
import { fetcherFull } from "utils/api";
import { useRateLimit, sanitiseInput } from "utils/utils.jsx";
import { totpField } from "utils/validation";
import { toast } from "react-toastify";

const TotpVerifyDialog = ({
    open,
    onClose,
    onVerifySuccess,
    customVerifyFunction = null
}) => {
    const handleClose = () => {
        onClose();
    };
    const { isBlocked, incrementAttempts, resetAttempts } = useRateLimit();

    const initialValues = { totp: "" };

    const validationSchema = Yup.object().shape({
        totp: totpField.required("2FA token is required"),
    });

    const defaultVerifyTOTP = async (totp) => {
        const sanitisedTotp = sanitiseInput(totp?.replace(/\s+/g, ""));
        return fetcherFull("/v2/auth/totp", "POST", { code: sanitisedTotp });
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        if (isBlocked) {
            toast.error('Too many attempts. Please try again later.');
            return;
        }
        try {
            const verifyFunction = customVerifyFunction || defaultVerifyTOTP;
            await verifyFunction(values.totp);
            resetAttempts();
            onVerifySuccess(values.totp);
            onClose();
        } catch (error) {
            incrementAttempts();
            toast.error('Invalid 2FA token or network error. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            handler={handleClose}
            className="dialog"
        >
            <div className="bg-white rounded-xl">
                <DialogHeader className="dialog__header">
                        Verify Your Identity
                </DialogHeader>
                <Formik
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    validationSchema={validationSchema}
                >
                    {({ isValid, dirty, isSubmitting }) => (
                        <Form>
                            <DialogBody className="dialog__body">
                                <div className="font-body py-4">
                                    <p className="text-primary">
                                        Enter the passcode from your authentication application
                                    </p>
                                    {isBlocked && (
                                        <Alert color="red" className="mt-2">
                                            Too many failed attempts. Please try again later.
                                        </Alert>
                                    )}
                                    <FormikInput
                                        autoComplete="one-time-code"
                                        className="font-display text-lg bg-white pt-2"
                                        name="totp"
                                        type="text"
                                        color="black"
                                        label="Authentication Code"
                                        required
                                    />
                                </div>
                            </DialogBody>
                            <DialogFooter className="dialog__footer pt-0">
                                <div>
                                    <Button
                                        type="button"
                                        className="btn btn--primary mx-2"
                                        variant="filled"
                                        onClick={handleClose}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        className="btn btn--primary"
                                        variant="filled"
                                        disabled={!(isValid && dirty) || isSubmitting || isBlocked}
                                    >
                                        {isSubmitting ? 'Verifying...' : 'Verify'}
                                    </Button>
                                </div>
                            </DialogFooter>
                        </Form>
                    )}
                </Formik>
            </div>
        </Dialog>
    );
};

export default TotpVerifyDialog;
